import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api';
import { XMarkIcon } from './icons';

// Utility to generate a pseudo-random UUID for anonymous users
const generateAnonymousId = () => {
    return 'anon_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
};

export const CookieConsent: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Slight delay so it doesn't pop up aggressively the instant the page loads
            const timer = setTimeout(() => setIsVisible(true), 1500);
            return () => clearTimeout(timer);
        }

        // Ensure anonymousId exists even if consent was already given (for future tracking)
        if (!localStorage.getItem('anonymousId')) {
            localStorage.setItem('anonymousId', generateAnonymousId());
        }
    }, []);

    const logConsent = async (status: 'accepted' | 'rejected') => {
        try {
            let anonymousId = localStorage.getItem('anonymousId');
            if (!anonymousId) {
                anonymousId = generateAnonymousId();
                localStorage.setItem('anonymousId', anonymousId);
            }

            // Check if user is logged in by looking for token and extracting ID (basic implementation)
            // In a real app, you might pass userId as a prop or from a global auth context
            const userStr = localStorage.getItem('user');
            let userId = null;
            if (userStr) {
                try {
                    const user = JSON.parse(userStr);
                    userId = user?.id;
                } catch (e) {
                    console.error("Error parsing user from local storage");
                }
            }

            // In production or local backend, adapt the URL format
            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                ? `http://${window.location.hostname}:3004`
                : '';

            await apiFetch(`${baseUrl}/api/cookie-consent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    anonymousId,
                    userId,
                    consentStatus: status
                })
            });
        } catch (error) {
            console.error('Failed to log cookie consent to server:', error);
            // We ignore errors here so it doesn't break the user experience
        }
    };

    const handleAccept = () => {
        localStorage.setItem('cookieConsent', 'accepted');
        setIsVisible(false);
        logConsent('accepted');
    };

    const handleReject = () => {
        localStorage.setItem('cookieConsent', 'rejected');
        setIsVisible(false);
        logConsent('rejected');
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-md animate-slide-up">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 overflow-hidden relative">
                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>

                <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                        <span>🍪</span> Çerez Kullanımı
                    </h3>
                    <button
                        onClick={() => setIsVisible(false)}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        aria-label="Kapat"
                        title="Kapat"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-300 mb-5 leading-relaxed">
                    Sizlere daha iyi bir hizmet sunabilmek, platform deneyiminizi iyileştirmek ve kişiselleştirmek için çerezleri kullanıyoruz. Çerez tercihlerinizi yönetebilir veya onaylayabilirsiniz. Daha fazla bilgi için <a href="/kullanici-sozlesmesi" className="text-blue-500 hover:text-blue-600 hover:underline">Gizlilik Politikamızı</a> inceleyebilirsiniz.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={handleAccept}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-xl transition-colors text-sm text-center"
                    >
                        Tümünü Kabul Et
                    </button>
                    <button
                        onClick={handleReject}
                        className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 font-medium py-2.5 px-4 rounded-xl transition-colors text-sm text-center"
                    >
                        Reddet
                    </button>
                </div>
            </div>
        </div>
    );
};
